import { BOOK_CLUB_QR_DATA_URL } from "./bookClubQr";

const CARD_SANS = '"PingFang SC","Microsoft YaHei",sans-serif';

export const CARD_TEMPLATES = [
  { key: "dawn", label: "晨光", swatch: "linear-gradient(160deg,#8ec4ef,#6a9a62)" },
  { key: "dusk", label: "暮色", swatch: "linear-gradient(160deg,#f6c28a,#c45c4a)" },
  { key: "sea", label: "海边", swatch: "linear-gradient(160deg,#d7eef6,#3d7d90)" },
  { key: "night", label: "夜读", swatch: "linear-gradient(160deg,#1c3a5f,#0b1220)" },
  { key: "ink", label: "水墨", swatch: "linear-gradient(160deg,#f3efe6,#4a4a46)" },
];

export function normalizeTemplate(key) {
  if (key === "warm") return "dusk";
  if (key === "minimal") return "ink";
  if (CARD_TEMPLATES.some((t) => t.key === key)) return key;
  return "dawn";
}

function prettyDate(raw) {
  const m = String(raw || "").match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return raw || "";
  return `${Number(m[2])}月${Number(m[3])}日`;
}

function wrapCardText(ctx, text, maxWidth, maxLines = 6) {
  const punct = "。！？，、；：";
  const chars = Array.from(String(text || "").replace(/\s+/g, ""));
  const lines = [];
  let line = "";
  chars.forEach((ch) => {
    if (!line && punct.includes(ch) && lines.length) {
      lines[lines.length - 1] += ch;
      return;
    }
    const next = line + ch;
    if (ctx.measureText(next).width > maxWidth && line) {
      if (punct.includes(ch)) {
        line += ch;
        lines.push(line);
        line = "";
        return;
      }
      lines.push(line);
      line = ch;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  if (lines.length > 1 && lines[lines.length - 1].length === 1 && punct.includes(lines[lines.length - 1])) {
    lines[lines.length - 2] += lines.pop();
  }
  return lines.slice(0, maxLines);
}

function fillCenter(ctx, text, y) {
  ctx.fillText(text, (1080 - ctx.measureText(text).width) / 2, y);
}

function ridge(ctx, w, photoH, color, base, amp, step) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, photoH);
  for (let x = 0; x <= w; x += 24) {
    const y = base + Math.sin(x / step) * amp + Math.sin(x / (step * 0.37)) * amp * 0.35;
    ctx.lineTo(x, y);
  }
  ctx.lineTo(w, photoH);
  ctx.closePath();
  ctx.fill();
}

function paintDawn(ctx, w, photoH) {
  const sky = ctx.createLinearGradient(0, 0, 0, photoH);
  sky.addColorStop(0, "#6eaddf");
  sky.addColorStop(0.42, "#b9d7ef");
  sky.addColorStop(1, "#e4efe4");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, photoH);

  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.beginPath();
  ctx.ellipse(220, 90, 150, 36, 0, 0, Math.PI * 2);
  ctx.ellipse(340, 78, 90, 28, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(820, 120, 170, 34, 0, 0, Math.PI * 2);
  ctx.fill();

  const mist = ctx.createLinearGradient(0, photoH * 0.38, 0, photoH * 0.72);
  mist.addColorStop(0, "rgba(255,255,255,0)");
  mist.addColorStop(1, "rgba(255,255,255,0.55)");
  ctx.fillStyle = mist;
  ctx.fillRect(0, photoH * 0.38, w, photoH * 0.34);

  ridge(ctx, w, photoH, "#9ec4c8", photoH * 0.58, 28, 220);
  ridge(ctx, w, photoH, "#7eaa86", photoH * 0.68, 36, 160);
  ridge(ctx, w, photoH, "#5f8f62", photoH * 0.78, 22, 120);

  ctx.fillStyle = "#4f7d49";
  for (let i = 0; i < 5; i += 1) {
    const y = photoH * 0.8 + i * 28;
    ctx.beginPath();
    ctx.moveTo(0, y + 18);
    for (let x = 0; x <= w; x += 20) {
      ctx.lineTo(x, y + Math.sin(x / 90 + i) * 8);
    }
    ctx.lineTo(w, photoH);
    ctx.lineTo(0, photoH);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = i % 2 ? "#3f6b3d" : "#568a4c";
  }
}

function paintDusk(ctx, w, photoH) {
  const sky = ctx.createLinearGradient(0, 0, 0, photoH);
  sky.addColorStop(0, "#f6d7b0");
  sky.addColorStop(0.38, "#f0a06a");
  sky.addColorStop(0.72, "#d36a58");
  sky.addColorStop(1, "#8d4a52");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, photoH);

  const sun = ctx.createRadialGradient(760, 210, 10, 760, 210, 150);
  sun.addColorStop(0, "rgba(255,244,220,0.95)");
  sun.addColorStop(1, "rgba(255,180,90,0)");
  ctx.fillStyle = sun;
  ctx.beginPath();
  ctx.arc(760, 210, 150, 0, Math.PI * 2);
  ctx.fill();

  ridge(ctx, w, photoH, "#6d4458", photoH * 0.58, 34, 200);
  ridge(ctx, w, photoH, "#4a3044", photoH * 0.7, 26, 140);
  ridge(ctx, w, photoH, "#2c2430", photoH * 0.82, 16, 90);
}

function paintSea(ctx, w, photoH) {
  const sky = ctx.createLinearGradient(0, 0, 0, photoH * 0.55);
  sky.addColorStop(0, "#c5e4f2");
  sky.addColorStop(1, "#f7f1e4");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, photoH * 0.58);

  ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.beginPath();
  ctx.ellipse(180, 70, 120, 26, 0, 0, Math.PI * 2);
  ctx.ellipse(280, 62, 70, 18, 0, 0, Math.PI * 2);
  ctx.fill();

  const sun = ctx.createRadialGradient(540, 250, 8, 540, 250, 90);
  sun.addColorStop(0, "rgba(255,248,230,0.95)");
  sun.addColorStop(1, "rgba(255,220,160,0)");
  ctx.fillStyle = sun;
  ctx.beginPath();
  ctx.arc(540, 250, 90, 0, Math.PI * 2);
  ctx.fill();

  const seaY = photoH * 0.52;
  const bands = ["#b7d5df", "#8fbfcf", "#5f9eb4", "#3d7f96", "#2f657c"];
  bands.forEach((color, i) => {
    const y = seaY + i * ((photoH - seaY) / bands.length);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, y + 10);
    for (let x = 0; x <= w; x += 16) {
      ctx.lineTo(x, y + Math.sin(x / 80 + i) * 6);
    }
    ctx.lineTo(w, photoH);
    ctx.lineTo(0, photoH);
    ctx.closePath();
    ctx.fill();
  });
}

function paintNight(ctx, w, photoH) {
  const sky = ctx.createLinearGradient(0, 0, 0, photoH);
  sky.addColorStop(0, "#0c1830");
  sky.addColorStop(0.55, "#1a3d66");
  sky.addColorStop(1, "#243044");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, photoH);

  ctx.fillStyle = "#f4efe4";
  for (let i = 0; i < 46; i += 1) {
    const x = (i * 137) % w;
    const y = (i * 53) % Math.floor(photoH * 0.5);
    const r = i % 7 === 0 ? 2.2 : 1.1;
    ctx.globalAlpha = 0.45 + (i % 5) * 0.1;
    ctx.beginPath();
    ctx.arc(x, y + 24, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  const moon = ctx.createRadialGradient(820, 120, 8, 820, 120, 70);
  moon.addColorStop(0, "rgba(255,248,230,0.95)");
  moon.addColorStop(1, "rgba(255,248,230,0)");
  ctx.fillStyle = moon;
  ctx.beginPath();
  ctx.arc(820, 120, 70, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#f7f1e2";
  ctx.beginPath();
  ctx.arc(820, 120, 28, 0, Math.PI * 2);
  ctx.fill();

  ridge(ctx, w, photoH, "#16304a", photoH * 0.62, 30, 180);
  ridge(ctx, w, photoH, "#0e1c30", photoH * 0.76, 18, 110);
}

function paintInk(ctx, w, photoH) {
  const paper = ctx.createLinearGradient(0, 0, 0, photoH);
  paper.addColorStop(0, "#f6f3ec");
  paper.addColorStop(1, "#e4dfd4");
  ctx.fillStyle = paper;
  ctx.fillRect(0, 0, w, photoH);

  ctx.fillStyle = "rgba(80,80,76,0.08)";
  ctx.beginPath();
  ctx.ellipse(200, 160, 260, 70, 0, 0, Math.PI * 2);
  ctx.ellipse(780, 220, 300, 80, 0, 0, Math.PI * 2);
  ctx.fill();

  const washes = [
    ["rgba(70,70,66,0.18)", 0.5, 40, 240],
    ["rgba(50,50,48,0.28)", 0.64, 28, 170],
    ["rgba(30,30,28,0.45)", 0.78, 18, 120],
  ];
  washes.forEach(([color, base, amp, step]) => {
    ridge(ctx, w, photoH, color, photoH * base, amp, step);
  });

  ctx.strokeStyle = "rgba(40,40,38,0.35)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(160, photoH * 0.72);
  ctx.quadraticCurveTo(210, photoH * 0.42, 250, photoH * 0.7);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(250, photoH * 0.55);
  ctx.quadraticCurveTo(300, photoH * 0.38, 280, photoH * 0.62);
  ctx.stroke();
}

const SCENES = {
  dawn: paintDawn,
  dusk: paintDusk,
  sea: paintSea,
  night: paintNight,
  ink: paintInk,
};

/** cover 方式绘制用户照片头部 */
function paintPhotoHeader(ctx, w, photoH, img) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) {
    paintDawn(ctx, w, photoH);
    return;
  }
  const scale = Math.max(w / iw, photoH / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  const dx = (w - dw) / 2;
  const dy = (photoH - dh) / 2;
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, w, photoH);
  ctx.clip();
  ctx.drawImage(img, dx, dy, dw, dh);
  ctx.restore();

  // 轻微暗角，让下方白底过渡更自然
  const veil = ctx.createLinearGradient(0, photoH * 0.55, 0, photoH);
  veil.addColorStop(0, "rgba(0,0,0,0)");
  veil.addColorStop(1, "rgba(0,0,0,0.18)");
  ctx.fillStyle = veil;
  ctx.fillRect(0, photoH * 0.55, w, photoH * 0.45);
}

export function loadCardHeaderImage(src) {
  return new Promise((resolve, reject) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("照片加载失败"));
    img.src = src;
  });
}

const QR_CANDIDATES = [
  BOOK_CLUB_QR_DATA_URL,
  "/book-club-qr.png",
  "/uploads/book-club-qr.png",
  "/static/book-club-qr.png",
];
let qrImagePromise = null;

function loadImageQuiet(src) {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    // data URL 不需要 crossOrigin；外链/同域图片再开
    if (!String(src).startsWith("data:")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** 站点首页二维码（优先内嵌 data URL，不依赖静态文件是否已部署） */
export function loadBookClubQrImage() {
  if (!qrImagePromise) {
    qrImagePromise = (async () => {
      for (const src of QR_CANDIDATES) {
        const img = await loadImageQuiet(src);
        if (img) return img;
      }
      return null;
    })();
  }
  return qrImagePromise;
}

function paintFooterWithQr(ctx, w, qrImage) {
  const dashY = 1168;
  ctx.save();
  ctx.strokeStyle = "#d5d5d0";
  ctx.setLineDash([8, 8]);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(120, dashY);
  ctx.lineTo(960, dashY);
  ctx.stroke();
  ctx.restore();

  const qrSize = 148;
  const qrX = w - 120 - qrSize;
  const qrY = 1200;

  if (qrImage) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(qrX - 8, qrY - 8, qrSize + 16, qrSize + 16);
    ctx.strokeStyle = "#e6e6e0";
    ctx.lineWidth = 2;
    ctx.strokeRect(qrX - 8, qrY - 8, qrSize + 16, qrSize + 16);
    ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
    ctx.fillStyle = "#a8a8a2";
    ctx.font = `20px ${CARD_SANS}`;
    const tip = "扫码了解";
    ctx.fillText(tip, qrX + (qrSize - ctx.measureText(tip).width) / 2, qrY + qrSize + 28);
  }

  const textMaxX = qrImage ? qrX - 36 : w - 120;
  ctx.fillStyle = "#b0b0aa";
  ctx.font = `24px ${CARD_SANS}`;
  const line1 = "喜欢读书  ·  喜欢学习  ·  喜欢成长";
  const line2 = "婚恋有道  ·  晨读";
  const startX = 120;
  // 有二维码时左对齐，避免和二维码重叠；没有则居中
  if (qrImage) {
    ctx.fillText(line1, startX, 1268);
    ctx.font = `22px ${CARD_SANS}`;
    ctx.fillText(line2, startX, 1324);
    ctx.fillStyle = "#c0c0ba";
    ctx.font = `18px ${CARD_SANS}`;
    ctx.fillText("hunlian.xbjw.org", startX, 1372);
  } else {
    fillCenter(ctx, line1, 1268);
    ctx.font = `22px ${CARD_SANS}`;
    fillCenter(ctx, line2, 1324);
  }
  void textMaxX;
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {{ text: string, bookTitle?: string, template?: string, dateLabel?: string, headerImage?: CanvasImageSource|null, qrImage?: CanvasImageSource|null }} opts
 */
export function paintHarvestCard(canvas, { text, bookTitle, template, dateLabel, headerImage, qrImage }) {
  const w = 1080;
  const h = 1440;
  const photoH = 560;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  const key = normalizeTemplate(template);

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);

  if (headerImage) {
    paintPhotoHeader(ctx, w, photoH, headerImage);
  } else {
    (SCENES[key] || paintDawn)(ctx, w, photoH);
  }

  const fade = ctx.createLinearGradient(0, photoH - 48, 0, photoH + 8);
  fade.addColorStop(0, "rgba(255,255,255,0)");
  fade.addColorStop(1, "#ffffff");
  ctx.fillStyle = fade;
  ctx.fillRect(0, photoH - 48, w, 56);

  ctx.fillStyle = "#8a8f86";
  ctx.font = `26px ${CARD_SANS}`;
  fillCenter(ctx, bookTitle ? `《${bookTitle}》` : "今日晨读", 700);
  ctx.font = `22px ${CARD_SANS}`;
  fillCenter(ctx, prettyDate(dateLabel), 748);

  let fontSize = 52;
  let lines = [];
  while (fontSize >= 32) {
    ctx.font = `${fontSize}px ${CARD_SANS}`;
    lines = wrapCardText(ctx, text, 820, 8);
    if (lines.length <= 5) break;
    fontSize -= 4;
  }
  const lineH = Math.round(fontSize * 1.55);
  const block = lines.length * lineH;
  let y = 820 + Math.max(0, (220 - block) / 2);
  ctx.fillStyle = "#2c2c2c";
  ctx.font = `${fontSize}px ${CARD_SANS}`;
  lines.forEach((ln) => {
    fillCenter(ctx, ln, y);
    y += lineH;
  });

  ctx.fillStyle = "#9aa097";
  ctx.font = `26px ${CARD_SANS}`;
  fillCenter(ctx, "这句话，我很认同", Math.min(y + 28, 1100));

  paintFooterWithQr(ctx, w, qrImage || null);
}

/** 异步绘制：支持 headerImageUrl（本机 blob 或站点 /uploads 路径） */
export async function paintHarvestCardAsync(canvas, opts) {
  let headerImage = opts.headerImage || null;
  if (!headerImage && opts.headerImageUrl) {
    try {
      headerImage = await loadCardHeaderImage(opts.headerImageUrl);
    } catch {
      headerImage = null;
    }
  }
  let qrImage = opts.qrImage || null;
  if (!qrImage) {
    try {
      qrImage = await loadBookClubQrImage();
    } catch {
      qrImage = null;
    }
  }
  paintHarvestCard(canvas, { ...opts, headerImage, qrImage });
}
